import { Link } from "react-router-dom";
import { Info, ExternalLink, BookOpen, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useT } from "@/i18n/useT";
import { localePath } from "@/i18n/locales";
import { API_PUBLIC_BASE, PUBLIC_ENDPOINTS, DATA_SOURCE_URL, DOMAIN } from "@/config/app";
import { WATCH_KEYS, PRESSURE_KEYS } from "@/config/fng";
import Sparkline from "@/components/Sparkline";
import CopyButton from "@/components/CopyButton";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import NextUpdateCountdown from "@/components/NextUpdateCountdown";
import { usePulse, type PulseData } from "@/hooks/use-pulse";
import { useTape, type TapeData } from "@/hooks/use-tape";
import type { MessageKey } from "@/i18n/messages";

interface InfoDrawerProps {
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  data: { color: string; next_update_at: string; value: number; bucket: string };
  last7: number[];
  last30: number[];
  contextData?: { summary?: string; trend_hint?: string; pulse?: string } | null;
}

function DirectionIcon({ direction }: { direction: string }) {
  if (direction === "rising") return <TrendingUp size={12} className="text-positive" />;
  if (direction === "falling") return <TrendingDown size={12} className="text-negative" />;
  return <Minus size={12} className="text-muted-foreground" />;
}

function PulseSection({ pulse, t }: { pulse: PulseData; t: (key: MessageKey, params?: Record<string, string | number>) => string }) {
  const deltaSign = pulse.delta_1d > 0 ? "+" : "";

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-foreground">{t("infoRegimePulse")}</h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Δ 1d</span>
          <span className={`font-mono font-medium ${pulse.delta_1d > 0 ? "text-positive" : pulse.delta_1d < 0 ? "text-negative" : "text-foreground"}`}>
            {deltaSign}{pulse.delta_1d}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Ø 7d</span>
          <span className="font-mono font-medium text-foreground">{pulse.avg_7d}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Ø 30d</span>
          <span className="font-mono font-medium text-foreground">{pulse.avg_30d}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">vs 50</span>
          <span className="font-mono font-medium text-foreground">
            {pulse.distance_from_neutral > 0 ? "+" : ""}{pulse.distance_from_neutral}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">slope 7d</span>
          <span className="flex items-center gap-1 font-mono font-medium text-foreground">
            {pulse.slope_7d} <DirectionIcon direction={pulse.direction_7d} />
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">regime</span>
          <span className="font-mono font-medium text-foreground">{pulse.regime_duration}d</span>
        </div>
      </div>
      {pulse.watch !== "normal" && (
        <div className="flex items-center gap-2 mt-1">
          <span
            className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
            style={{
              backgroundColor: `${pulse.color}18`,
              color: pulse.color,
            }}
          >
            {(WATCH_KEYS as Record<string, string>)[pulse.watch] ? t((WATCH_KEYS as Record<string, string>)[pulse.watch] as MessageKey) : pulse.watch}
          </span>
        </div>
      )}
    </div>
  );
}

function formatMarketCap(usd: number): string {
  if (usd >= 1e12) return `$${(usd / 1e12).toFixed(2)}T`;
  if (usd >= 1e9) return `$${(usd / 1e9).toFixed(1)}B`;
  return `$${(usd / 1e6).toFixed(0)}M`;
}

function fmtChange(n: number | null): string {
  if (n === null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

function changeColor(n: number | null): string {
  if (n === null) return "text-foreground";
  if (n > 0) return "text-positive";
  if (n < 0) return "text-negative";
  return "text-foreground";
}

function TapeSection({ tape, t }: { tape: TapeData; t: (key: MessageKey, params?: Record<string, string | number>) => string }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-foreground">{t("infoMarketTape")}</h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">BTC 24h</span>
          <span className={`font-mono font-medium ${changeColor(tape.btc_change_24h)}`}>{fmtChange(tape.btc_change_24h)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">ETH 24h</span>
          <span className={`font-mono font-medium ${changeColor(tape.eth_change_24h)}`}>{fmtChange(tape.eth_change_24h)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Mkt Cap</span>
          <span className="font-mono font-medium text-foreground">{formatMarketCap(tape.total_market_cap_usd)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">BTC Dom.</span>
          <span className="font-mono font-medium text-foreground">{tape.bitcoin_percentage_of_market_cap}%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">24h Vol.</span>
          <span className="font-mono font-medium text-foreground">{formatMarketCap(tape.total_24h_volume_usd)}</span>
        </div>
        {tape.market_pressure && (
          <div className="flex items-center justify-between col-span-2">
            <span className="text-muted-foreground">{t("infoPressure")}</span>
            <span className="font-mono font-medium text-foreground capitalize">
              {PRESSURE_KEYS[tape.market_pressure] ? t(PRESSURE_KEYS[tape.market_pressure] as MessageKey) : tape.market_pressure}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InfoDrawer({ drawerOpen, setDrawerOpen, data, last7, last30, contextData }: InfoDrawerProps) {
  const { t, locale } = useT();
  const { data: pulseData } = usePulse(drawerOpen);
  const { data: tapeData } = useTape(drawerOpen);

  const endpointEntries = Object.values(PUBLIC_ENDPOINTS);

  return (
    <Drawer onOpenChange={setDrawerOpen}>
      <DrawerTrigger asChild>
        <button
          className="p-2 rounded-full text-muted-foreground/45 hover:text-muted-foreground/70 transition-colors"
          aria-label={t("infoButton")}
          onClick={(e) => e.stopPropagation()}
        >
          <Info size={15} strokeWidth={1.5} />
        </button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle className="text-base">{t("infoTitle")}</DrawerTitle>
          <DrawerDescription className="text-sm leading-relaxed">
            {t("infoDescription")}
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-8 overflow-y-auto">
          {/* Analytics section */}
          <div className="space-y-4">
            {/* Sparklines */}
            {last7.length >= 2 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">{t("info7d")}</span>
                  <Sparkline values={last7} color={data.color} width={100} height={28} />
                </div>
                {last30.length >= 7 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{t("info30d")}</span>
                    <Sparkline values={last30} color={data.color} width={100} height={28} />
                  </div>
                )}
              </div>
            )}

            {/* Regime & Pulse analytics */}
            {pulseData && <PulseSection pulse={pulseData} t={t} />}

            {/* Market Tape */}
            {tapeData && <TapeSection tape={tapeData} t={t} />}

            {/* Next update */}
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground font-medium">{t("infoNextUpdate")}</span>
              <span className="font-mono text-foreground">
                <NextUpdateCountdown nextUpdateAt={data.next_update_at} />
              </span>
            </div>

            {/* Daily context */}
            {contextData?.summary && (
              <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                <p className="text-xs text-foreground leading-relaxed">{contextData.summary}</p>
                {contextData.trend_hint && (
                  <p className="text-xs text-muted-foreground">{contextData.trend_hint}</p>
                )}
              </div>
            )}

            {/* Market Pulse */}
            {contextData?.pulse && (
              <div className="flex items-start gap-2 px-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0 pt-px">{t("infoPulseTitle")}</span>
                <p className="text-xs text-foreground/80 leading-relaxed">{contextData.pulse}</p>
              </div>
            )}
          </div>

          {/* Reference section */}
          <div className="space-y-4 mt-5 pt-4 border-t border-border/60">
            {/* Interpretation guide */}
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-foreground">{t("infoInterpretTitle")}</h3>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="space-y-1">
                  <span className="font-medium text-foreground">0–24</span>
                  <p>{t("infoInterpretExtremeFear")}</p>
                </div>
                <div className="space-y-1">
                  <span className="font-medium text-foreground">75–100</span>
                  <p>{t("infoInterpretExtremeGreed")}</p>
                </div>
              </div>
            </div>

            {/* Attribution */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("infoAttribution")}
              </p>
              <a
                href={DATA_SOURCE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                alternative.me <ExternalLink size={10} />
              </a>
            </div>

            {/* Disclaimer */}
            <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
              {t("infoDisclaimer")}
            </p>
          </div>

          {/* API & Docs section */}
          <div className="space-y-3 mt-5 pt-4 border-t border-border/60">
            <h3 className="text-xs font-semibold text-foreground">{t("infoApiTitle")}</h3>
            <div className="space-y-1.5">
              {endpointEntries.map((ep) => (
                <CopyButton key={ep.public} text={`${API_PUBLIC_BASE}${ep.public}`} label={ep.label} />
              ))}
              <CopyButton text={`${DOMAIN}/llms.txt`} label="llms.txt" />
              <CopyButton text={`${DOMAIN}/openapi.json`} label="OpenAPI spec" />
            </div>
            <Link
              to={localePath(locale, "/about")}
              className="flex items-center gap-2 text-xs text-primary hover:underline"
              onClick={() => setDrawerOpen(false)}
            >
              <BookOpen size={12} />
              {t("infoDocsLink")}
            </Link>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
